<template>
    <v-container fluid class="white elevation-4 pt-0 pb-0">
        <main-app-bar title="Attendance"/>
        <v-row class="grey lighten-4 align-center">
            <v-col cols="4">
                <v-menu
                    v-model="monthMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="auto"
                    hide-details
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            v-model="month"
                            :disabled="dataLoading"
                            label="Month"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            hide-details
                        />
                    </template>
                    <v-date-picker
                        type="month"
                        v-model="month"
                        no-title
                        @input="monthMenu = false"
                        @change="getWorkLog"
                    />
                </v-menu>
            </v-col>
            <v-col cols="2">
                <v-btn large block color="primary" :disabled="dataLoading" @click="initGenerateWorklog">Update</v-btn>
            </v-col>
            <v-col cols="2">
                <div class="body-2">Last Updated</div>
                <div v-if="lastUpdated" class="body-2">{{Date.getDateAndTime(lastUpdated)}}</div>
                <div v-else>-- : --</div>
            </v-col>
            <v-col cols="4" class="py-0">
                <div v-if="isOutdated" class="d-flex align-center">
                    <v-icon color="error" size="40" class="mr-3">mdi-alert</v-icon>
                    <span class="red--text body-2"><strong>One or more leaves have been edited. The report might be outdated.</strong></span>
                </div>
            </v-col>
        </v-row>
        <v-row class="grey lighten-4 align-center">
            <v-col cols="4">
                <v-autocomplete
                    ref="employeeSelect"
                    v-model="chosenEmployee"
                    :items="employees"
                    :disabled="dataLoading"
                    item-text="value"
                    item-value="id"
                    dense
                    outlined
                    label="Select employee"
                    @change="getWorkLog"
                >
                    <template v-slot:selection="{ item }">
                        <span style="max-width: 200px;overflow: hidden" class="selection-text-style">{{ item.value }}</span>
                    </template>
                </v-autocomplete>
            </v-col>
            <v-col cols="4">
                <v-autocomplete
                    ref="employeeSelect"
                    v-model="chosenClinic"
                    :items="clinics"
                    :disabled="dataLoading"
                    item-text="value"
                    item-value="id"
                    dense
                    outlined
                    label="Select Unit"
                    @change="getWorkLog"
                >
                    <template v-slot:selection="{ item }">
                        <span style="max-width: 200px;overflow: hidden" class="selection-text-style">{{ item.value }}</span>
                    </template>
                </v-autocomplete>
            </v-col>
        </v-row>
        <v-row class="white">
            <v-col cols="12">
                <div style="border:1px solid #e0e0e0">
                    <v-data-table
                        :headers="headers"
                        :items="items"
                        :loading="dataLoading"
                        :footer-props="{
              'items-per-page-options': [10, 20, 30, -1],
              'show-current-page': true,
              'show-first-last-page': true
            }"
                        :items-per-page="20"
                        :show-current-page="true"
                        :fixed-header="true"
                        :height="windowWidth >= 1700 ? 700 : 500"
                    >
                        <template v-slot:item.employee="{ item }">
                            <div class="d-flex align-center">
                                <strong style="word-break: break-word">{{item.employee.value}}</strong>
                                <v-icon v-if="item.isOutdated" color="error" size="20" class="ml-1">mdi-alert</v-icon>
                            </div>
                        </template>
                        <template v-slot:item.bonus="{ item }">
                            <div v-if="item.bonus && !item.bonus.isCancelled">
                                <v-icon size="30" color="primary">mdi-check</v-icon>
                                <v-tooltip right max-width="300px">
                                    <template v-slot:activator="{ on }">
                                        <v-icon size="18" v-on="on">mdi-alert-circle</v-icon>
                                    </template>
                                    <div class="tooltip-fixed pa-1">
                                        <div class="mb-2"><strong>BONUS</strong></div>
                                        <div><strong>Approved by:</strong> {{item.bonus.updatedBy}}</div>
                                        <div><strong>Created on:</strong> {{Date.getDateAndTime(item.bonus.createdOn)}}</div>
                                        <div><strong>Comment:</strong> {{item.bonus.note}}</div>
                                    </div>
                                </v-tooltip>
                            </div>
                        </template>
                        <template v-slot:item.sickLeaveEpisodes="{ item }">
                            <v-btn small text @click="openSickDialog(item)" :class="{ 'sick-leave__bg': item.sickLeaveEpisodes }">Sick ({{item.sickLeaveEpisodes}})</v-btn>
                        </template>
                        <template v-slot:item.paidHolidays="{ item }">
                            <WorklogCell :value="item.paidHolidays" :item="item"/>
                        </template>
                        <template v-slot:item.bankHolidays="{ item }">
                            <WorklogCell :value="item.bankHolidays" :item="item"/>
                        </template>
                        <template v-slot:item.collegeDays="{ item }">
                            <WorklogCell :value="item.collegeDays" :item="item"/>
                        </template>
                        <template v-slot:item.maternityAppointment="{ item }">
                            <WorklogCell :value="item.maternityAppointment" :item="item"/>
                        </template>
                        <template v-slot:item.maternityLeave="{ item }">
                            <WorklogCell :value="item.maternityLeave" :item="item"/>
                        </template>
                    </v-data-table>
                    <SickDialog v-if="sickDialog" :sickDialog="sickDialog" :sickEmployee="sickEmployee" @closeDialog="sickDialog = false"/>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import MainAppBar from '@/components/MainAppBar'
    import { mapActions } from 'vuex'
    import WorklogCell from '@/views/accounting/WorklogCell'
    import SickDialog from '@/views/accounting/SickDialog'
    export default {
        name: 'Worklog',
        components: { SickDialog, WorklogCell, MainAppBar },
        data: () => ({
            headers: [
                { text: '', align: 'start', value: 'employee', sortable: false, width: 200, divider: true },
                { text: 'Bonus', value: 'bonus', align: 'center', width: 85, divider: true },
                { text: 'Sick Leaves', value: 'sickLeaveEpisodes', align: 'center', divider: true },
                { text: 'Paid Holidays', value: 'paidHolidays', align: 'center' },
                { text: 'Bank Holidays', value: 'bankHolidays', align: 'center' },
                { text: 'College Days', value: 'collegeDays', align: 'center' },
                { text: 'Maternity Appointments', value: 'maternityAppointment', align: 'center' },
                { text: 'Maternity Leaves', value: 'maternityLeave', align: 'center' }
            ],
            items: [],
            dataLoading: false,
            month: new Date().getMonth() ? `${new Date().getFullYear()}-${('0' + new Date().getMonth()).slice(-2)}` : `${new Date().getFullYear() - 1}-${new Date().getMonth() + 12}`,
            monthMenu: false,
            employees: [],
            chosenEmployee: null,
            clinics: [],
            chosenClinic: null,
            lastUpdated: null,
            isOutdated: false,
            sickDialog: false,
            sickEmployee: {}
        }),
        mounted: function () {
            this.getWorkLog()
        },
        methods: {
            ...mapActions(['fetchMonthWorklog', 'generateWorklog']),
            getWorkLog: async function () {
                this.items = []
                try {
                    const queryData = {
                        Date: this.month,
                        EmployeeIds: this.chosenEmployee,
                        ClinicIds: this.chosenClinic
                    }
                    this.dataLoading = true
                    const worklogData = await this.fetchMonthWorklog(queryData)
                    worklogData.data.lastUpdated ? this.lastUpdated = worklogData.data.lastUpdated : this.lastUpdated = null
                    this.isOutdated = worklogData.data.isOutdated
                    this.items = worklogData.data.items
                    this.employees = worklogData.data.metadata.employees
                    this.clinics = worklogData.data.metadata.clinics
                    this.dataLoading = false
                } catch (e) {
                    this.dataLoading = false
                }
            },
            initGenerateWorklog: async function () {
                this.items = []
                try {
                    const queryData = {
                        date: this.month,
                        regenerateWholeReport: false
                    }
                    this.dataLoading = true
                    await this.generateWorklog(queryData)
                    await this.getWorkLog()
                    this.dataLoading = false
                } catch (e) {
                    this.dataLoading = false
                }
            },
            openSickDialog: function (item) {
                this.sickDialog = true
                this.sickEmployee = {
                    employeeId: item.employee.id,
                    name: item.employee.value,
                    date: this.month
                }
            }
        },
        computed: {
            windowWidth: function () {
                return window.innerWidth
            }
        }
    }
</script>
<style lang="scss" scoped>
    .sick-leave__bg {
        background-color: #ffbf00;
    }
</style>
